import React from 'react';
import { Button, useToast } from '@chatsdet/ui';
import { Copy, CircleNotch } from '@phosphor-icons/react';

type AgentPromptButtonProps = {
  /** `GET <entity>/:id/agent-prompt` — returns the markdown to paste into a coding agent. */
  promptUrl: string;
  label?: string;
  /** Overrides the button's tooltip/aria-label; defaults to `label`. */
  tooltip?: string;
  /** Icon-only, for dense header action rails that already spell out their other actions as icons. */
  iconOnly?: boolean;
};

/** Copies an entity's agent prompt to the clipboard. One button for every entity that serves a prompt. */
export function AgentPromptButton({
  promptUrl,
  label = 'Copy agent prompt',
  tooltip,
  iconOnly = false,
}: AgentPromptButtonProps) {
  const [isCopying, setIsCopying] = React.useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    setIsCopying(true);
    try {
      const response = await fetch(promptUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to fetch agent prompt');
      }
      await navigator.clipboard.writeText(await response.text());
      toast.success('Agent prompt copied');
    } catch {
      toast.error('Failed to copy agent prompt');
    } finally {
      setIsCopying(false);
    }
  };

  const icon = isCopying ? (
    <CircleNotch size={16} className="animate-spin" aria-hidden="true" />
  ) : (
    <Copy size={16} aria-hidden="true" />
  );

  if (iconOnly) {
    return (
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip={tooltip ?? label}
        aria-label={tooltip ?? label}
        isDisabled={isCopying}
        onPress={handleCopy}
      >
        {icon}
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      iconLeft={icon}
      tooltip={tooltip}
      aria-label={label}
      isDisabled={isCopying}
      onPress={handleCopy}
    >
      {label}
    </Button>
  );
}
